import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import MuxPlayer from "@mux/mux-player-react";
import { ChevronDown, ChevronRight, Globe, Play } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { cn, formatDuration } from "@/lib/utils";

type InternalPlaylist = {
  id: string;
  name: string;
  description: string | null;
  visibility: string;
  publicSlug: string | null;
  videoCount: number;
};

type PlaylistItem = {
  videoId: string;
  title: string;
  category: string;
  accessTier: string;
  status: string;
  durationSeconds: number | null;
  hasPlayback: boolean;
  thumbnailUrl: string | null;
  source: "mux" | "youtube" | null;
  youtubeVideoId: string | null;
};

// Internal preview is decoupled from the recipient flow on purpose: it reuses
// the admin per-video playback signer (video.viewLibrary), so a teammate can
// watch without an issuance, NDA, or tier gate.
function PreviewPlayer({ item }: { item: PlaylistItem }) {
  const { data, isError } = useQuery({
    queryKey: ["admin-playback", item.videoId],
    queryFn: () =>
      api.post<{ playbackId: string; tokens: { playback: string; thumbnail: string; storyboard: string }; versionNumber: number }>(
        `/api/admin/videos/${item.videoId}/playback`,
      ),
    enabled: item.source !== "youtube",
    retry: false,
  });

  if (item.source === "youtube" && item.youtubeVideoId) {
    return (
      <iframe
        className="w-full h-full"
        src={`https://www.youtube.com/embed/${item.youtubeVideoId}`}
        title={item.title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }
  if (isError) {
    return (
      <div className="w-full h-full flex items-center justify-center text-white/70 text-sm">
        No preview available — the upload may still be transcoding.
      </div>
    );
  }
  if (!data?.playbackId) {
    return (
      <div className="w-full h-full flex items-center justify-center text-white/70 text-sm">
        Loading preview…
      </div>
    );
  }
  return (
    <MuxPlayer
      playbackId={data.playbackId}
      tokens={data.tokens}
      autoPlay
      streamType="on-demand"
      accentColor="#E6AC2A"
      className="w-full h-full"
    />
  );
}

function PlaylistRow({ pl }: { pl: InternalPlaylist }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<PlaylistItem | null>(null);
  const { data } = useQuery({
    queryKey: ["playlist", pl.id],
    queryFn: () =>
      api.get<{ playlist: unknown; items: PlaylistItem[] }>(
        `/api/admin/playlists/${pl.id}`,
      ),
    enabled: open,
  });
  const items = data?.items ?? [];

  return (
    <div className="border-b border-admin-rule last:border-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-foreground/5"
      >
        {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="font-medium flex items-center gap-2">
            {pl.name}
            <Badge variant="outline">{pl.visibility}</Badge>
            {pl.visibility === "public" && pl.publicSlug && (
              <a
                href={`/p/${pl.publicSlug}`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-xs text-electric hover:underline"
              >
                <Globe className="h-3 w-3" /> /p/{pl.publicSlug}
              </a>
            )}
          </div>
          {pl.description && (
            <div className="text-xs text-muted-foreground line-clamp-1">{pl.description}</div>
          )}
        </div>
        <span className="text-xs text-muted-foreground shrink-0">
          {pl.videoCount} {pl.videoCount === 1 ? "video" : "videos"}
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-1.5">
          {items.length === 0 && (
            <p className="text-xs text-muted-foreground py-2">This playlist has no videos.</p>
          )}
          {items.map((it) => {
            const playable = it.source === "youtube" ? !!it.youtubeVideoId : it.hasPlayback;
            return (
              <button
                key={it.videoId}
                disabled={!playable}
                onClick={() => playable && setPreview(it)}
                className={cn(
                  "w-full flex items-center gap-3 p-2 rounded-md text-left",
                  playable ? "hover:bg-foreground/5 cursor-pointer" : "opacity-50 cursor-not-allowed",
                )}
              >
                <div className="w-20 aspect-video rounded bg-navy shrink-0 overflow-hidden relative">
                  {it.thumbnailUrl ? (
                    <img src={it.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                  ) : null}
                  {playable && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <Play className="h-4 w-4 text-white drop-shadow" fill="currentColor" />
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium line-clamp-1">{it.title}</div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                    <span>{formatDuration(it.durationSeconds)}</span>
                    <Badge variant="outline">{it.accessTier}</Badge>
                    {it.source === "youtube" && <Badge variant="outline">YouTube</Badge>}
                    {it.status !== "approved" && <Badge variant="outline">{it.status}</Badge>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{preview?.title}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video bg-navy rounded-md overflow-hidden">
            {preview && <PreviewPlayer item={preview} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function InternalPlaylistsPage() {
  const { data } = useQuery({
    queryKey: ["internal-playlists"],
    queryFn: () => api.get<{ items: InternalPlaylist[] }>("/api/admin/playlists/internal"),
  });
  const items = data?.items ?? [];

  return (
    <div className="space-y-4 max-w-5xl">
      <div>
        <h1 className="font-head text-2xl font-semibold">Internal Playlists</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Playlists the team can preview in-app without sending a Demo Pack. Tag a
          playlist <span className="font-medium">internal</span> (org-only) or{" "}
          <span className="font-medium">public</span> (also gets a shareable URL) from the
          playlist editor to list it here.
        </p>
      </div>
      <Card className="p-0 overflow-hidden">
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-12">
            No internal or public playlists yet.
          </p>
        ) : (
          items.map((pl) => <PlaylistRow key={pl.id} pl={pl} />)
        )}
      </Card>
    </div>
  );
}
